'use client';

import { useState, useCallback } from 'react';
import { ShareNetwork, SpinnerGap, Copy, Check, Eye, WarningCircle } from '@phosphor-icons/react';
import { Button } from '@/components/ui/button';
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogDescription,
} from '@/components/ui/dialog';

interface ChatShareButtonProps {
  sessionId: string;
}

export function ChatShareButton({ sessionId }: ChatShareButtonProps) {
  const [open, setOpen] = useState(false);
  const [loading, setLoading] = useState(false);
  const [shareUrl, setShareUrl] = useState<string | null>(null);
  const [copied, setCopied] = useState(false);
  const [error, setError] = useState<string | null>(null);

  const handleShare = useCallback(async () => {
    // If already shared, just open the modal
    if (shareUrl) {
      setOpen(true);
      return;
    }

    setLoading(true);
    setError(null);
    setOpen(true);

    try {
      const res = await fetch(`/api/sessions/${sessionId}`, {
        method: 'PATCH',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ isPublic: true }),
      });

      if (!res.ok) {
        const data = await res.json().catch(() => ({}));
        setError(data.error || 'Failed to create share link');
        setLoading(false);
        return;
      }

      const url = `${window.location.origin}/s/${sessionId}`;
      setShareUrl(url);
    } catch {
      setError('Failed to create share link');
    } finally {
      setLoading(false);
    }
  }, [sessionId, shareUrl]);

  const [copyError, setCopyError] = useState<string | null>(null);
  const handleCopy = useCallback(async () => {
    if (!shareUrl) return;
    setCopyError(null);
    try {
      await navigator.clipboard.writeText(shareUrl);
      setCopied(true);
      window.setTimeout(() => setCopied(false), 2000);
    } catch {
      // Permissions denied, non-secure context, or browser refused — don't
      // leave the user staring at an unchanged button.
      setCopyError('Copy blocked — select the URL and copy manually.');
      window.setTimeout(() => setCopyError(null), 4000);
    }
  }, [shareUrl]);

  return (
    <>
      <Button
        variant="outline"
        size="sm"
        onClick={handleShare}
        className="h-8 gap-1.5"
      >
        <ShareNetwork className="size-4" />
        <span className="text-xs">Share</span>
      </Button>

      <Dialog open={open} onOpenChange={setOpen}>
        <DialogContent className="sm:max-w-md overflow-hidden">
          <DialogHeader>
            <DialogTitle
              className="font-normal"
              style={{ fontFamily: "'Playfair Display', Georgia, serif", fontSize: 22, letterSpacing: -0.3 }}
            >
              Share this{' '}
              <span
                className="italic"
                style={{
                  background: 'linear-gradient(135deg, #6B8AFF, #A78BFA, #38BDF8)',
                  WebkitBackgroundClip: 'text',
                  WebkitTextFillColor: 'transparent',
                }}
              >
                chat.
              </span>
            </DialogTitle>
            <DialogDescription>
              Anyone with the link can read this conversation. Visualizations, prompts, and tool calls are all visible.
            </DialogDescription>
          </DialogHeader>

          {loading ? (
            <div
              className="flex flex-col items-center justify-center py-8 gap-2"
              role="status"
              aria-live="polite"
            >
              <SpinnerGap className="size-6 animate-spin text-violet-300" aria-hidden="true" />
              <span className="text-sm text-muted-foreground">Creating share link…</span>
            </div>
          ) : error ? (
            <div
              className="rounded-xl border border-destructive/20 bg-destructive/[0.04] px-4 py-3.5 flex items-start gap-3"
              role="alert"
              aria-live="assertive"
              aria-atomic="true"
            >
              <WarningCircle weight="duotone" className="size-5 shrink-0 mt-0.5 text-destructive" aria-hidden="true" />
              <div className="flex-1">
                <p className="text-sm font-medium text-destructive/95">Couldn&apos;t create share link</p>
                <p className="mt-0.5 text-xs text-muted-foreground/70">{error}</p>
                <Button
                  variant="outline"
                  size="sm"
                  onClick={() => {
                    setError(null);
                    setShareUrl(null);
                    handleShare();
                  }}
                  className="mt-3"
                >
                  Try again
                </Button>
              </div>
            </div>
          ) : shareUrl ? (
            <div className="space-y-3">
              <div className="flex items-stretch gap-2">
                <input
                  type="text"
                  readOnly
                  value={shareUrl}
                  onFocus={(e) => e.target.select()}
                  className="min-w-0 flex-1 rounded-lg border border-white/[0.08] bg-white/[0.03] px-3.5 py-2.5 text-sm font-mono text-foreground/90 outline-none focus:border-violet-500/40 focus:bg-white/[0.05] transition-colors"
                />
                <Button
                  size="sm"
                  onClick={handleCopy}
                  aria-label={copied ? 'Copied to clipboard' : 'Copy share link'}
                  className={`shrink-0 gap-1.5 transition-all ${
                    copied
                      ? 'bg-emerald-500/15 text-emerald-300 border border-emerald-500/30 hover:bg-emerald-500/20'
                      : 'bg-gradient-to-r from-[#3B5BD9] to-[#6B4FC7] text-white shadow-[0_4px_16px_rgba(59,91,217,0.3)] hover:scale-[1.02]'
                  }`}
                >
                  {copied ? (
                    <>
                      <Check weight="bold" className="size-3.5" aria-hidden="true" />
                      Copied
                    </>
                  ) : (
                    <>
                      <Copy weight="bold" className="size-3.5" aria-hidden="true" />
                      Copy
                    </>
                  )}
                </Button>
              </div>
              {/* Live regions: announce copy success/failure to screen readers
                  without taking visual focus away from the button. */}
              <div className="sr-only" role="status" aria-live="polite">
                {copied ? 'Share link copied to clipboard.' : ''}
              </div>
              {copyError && (
                <p
                  className="flex items-start gap-1.5 text-[12px] text-amber-300/85"
                  role="alert"
                  aria-live="assertive"
                >
                  <WarningCircle weight="duotone" className="size-3.5 shrink-0 mt-0.5" aria-hidden="true" />
                  <span>{copyError}</span>
                </p>
              )}
              <p className="flex items-start gap-1.5 text-[11px] text-muted-foreground/55">
                <Eye weight="duotone" className="size-3.5 shrink-0 mt-0.5" aria-hidden="true" />
                <span>You can revoke the link anytime by toggling sharing off in the chat settings.</span>
              </p>
            </div>
          ) : null}
        </DialogContent>
      </Dialog>
    </>
  );
}
